'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/inputs/date-range/styles.css';
import { Button } from 'BomdisoftComponents/buttons/index';
import { CalendarMonthRoundedIcon } from 'BomdisoftComponents/icons/calendar-month-rounded';
import { ChevronLeftIcon } from 'BomdisoftComponents/icons/chevron-left';
import { ChevronRightIcon } from 'BomdisoftComponents/icons/chevron-right';
import { KeyboardArrowDownRoundedIcon } from 'BomdisoftComponents/icons/keyboard-arrow-down-rounded';
import { addMonths, clampDay, customRange, type DateRangePreset, type DateRangeValue, dayKey, dayPosition, isSameDay, isSelectable, monthGrid, PRESET_ORDER, presetRange, startOfDay, weekStartFor } from 'BomdisoftComponents/inputs/date-range/range';
import { Menu } from 'BomdisoftComponents/menu/index';
import { Tooltip } from 'BomdisoftComponents/tooltip/index';
import { type ReactNode, useId, useMemo, useRef, useState } from 'react';

export type { DateRangePreset, DateRangeValue } from 'BomdisoftComponents/inputs/date-range/range';
export { addDays, customRange, dayKey, isLive, PRESET_HOURS, PRESET_ORDER, presetRange, startOfDay } from 'BomdisoftComponents/inputs/date-range/range';

export type DateRangePickerProps = {
  applyLabel: string;
  cancelLabel: string;
  /** Names the popover for a screen reader, and labels the trigger. */
  label: string;
  locale: string;
  /** The oldest selectable day. Events fall out of retention, so a calendar that
   *  lets the reader ask for a month with no rows in it is answering a question
   *  the data cannot. */
  min: Date;
  /** The newest selectable day. Defaults to today: no event has a future
   *  timestamp, so a future range is always empty. */
  max?: Date;
  nextMonthLabel: string;
  onChange: (value: DateRangeValue) => void;
  /** Which relative presets this surface offers, by supplying their labels.
   *
   *  PARTIAL on purpose, and the omission is how a caller declines a preset. The
   *  set was fixed at all five, which forced every consumer to offer `live` and
   *  a 90-day span whether or not they meant anything there: a document search
   *  has no "and keep up" reading, so clepit hand-rolled a whole calendar rather
   *  than ship a control with an option that answered nothing. A preset with no
   *  label is simply not drawn, so adding one here can never appear somewhere it
   *  makes no sense. */
  presetLabels: Partial<Record<Exclude<DateRangePreset, 'custom'>, string>>;
  previousMonthLabel: string;
  testId?: string;
  /** Replaces the trigger's own presentation, panel untouched.
   *
   *  The default trigger is Field-shaped, which is right where this control was
   *  first placed: a page header, beside a title, at the toolbar height. Dropped
   *  into a row of flat filter chips it is the only bordered, filled, taller
   *  thing there, and clepit's search palette shipped exactly that: three text
   *  chips and one button that did not belong to them. A surface that already
   *  owns a control shape passes it here rather than the package growing a
   *  variant per host row, and the row then styles all of its chips from one
   *  rule instead of two definitions that can drift. */
  triggerClassName?: string;
  value: DateRangeValue;
};

const MONTHS_SHOWN = 2;

/**
 * A range of days, chosen from presets or from the calendar.
 *
 * Presets are relative and recomputed on read; a custom range is two clicks on
 * the grid, applied together, so the surface behind it never refetches against
 * a half-chosen span.
 */
export const DateRangePicker = ({ applyLabel, cancelLabel, label, locale, min, max, nextMonthLabel, onChange, presetLabels, previousMonthLabel, testId, triggerClassName, value }: DateRangePickerProps): ReactNode => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const ceiling = max ?? new Date();

  const [start, setStart] = useState<Date | null>(null);
  const [end, setEnd] = useState<Date | null>(null);
  const [view, setView] = useState<Date>(() => addMonths(startOfDay(ceiling), 1 - MONTHS_SHOWN));

  // Every one of these reads UTC, because the days they render are UTC days. A
  // cell formatted in the reader's own zone shows the neighbouring date for
  // half the world, so the number under the cursor stops matching the day the
  // click actually selects.
  const days = useMemo(() => new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC', year: 'numeric' }), [locale]);
  const months = useMemo(() => new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC', year: 'numeric' }), [locale]);
  const weekdays = useMemo(() => new Intl.DateTimeFormat(locale, { timeZone: 'UTC', weekday: 'narrow' }), [locale]);
  const cellDay = useMemo(() => new Intl.DateTimeFormat(locale, { day: 'numeric', timeZone: 'UTC' }), [locale]);
  const cellFull = useMemo(() => new Intl.DateTimeFormat(locale, { dateStyle: 'full', timeZone: 'UTC' }), [locale]);
  const weekStart = useMemo(() => weekStartFor(locale), [locale]);

  const weekdayNames = useMemo(() => Array.from({ length: 7 }, (_, index) => weekdays.format(new Date(Date.UTC(2024, 0, 7 + ((weekStart + index) % 7))))), [weekStart, weekdays]);

  // The exclusive upper bound names the day AFTER the last one selected, so the
  // trigger has to step back to say which day the reader actually picked.
  const named = (first: Date, last: Date): string => (isSameDay(first, last) ? days.format(first) : `${days.format(first)} - ${days.format(last)}`);
  const shownRange = named(new Date(value.from), new Date(new Date(value.to).getTime() - 1));
  const triggerText = value.preset === 'custom' ? shownRange : (presetLabels[value.preset] ?? shownRange);
  const offered = PRESET_ORDER.filter(preset => presetLabels[preset] !== undefined);

  const openPicker = () => {
    const from = startOfDay(new Date(value.from));
    const to = startOfDay(new Date(new Date(value.to).getTime() - 1));
    setStart(clampDay(from, min, ceiling));
    setEnd(clampDay(to, min, ceiling));
    setView(addMonths(clampDay(to, min, ceiling), 1 - MONTHS_SHOWN));
    setOpen(true);
  };

  const choosePreset = (preset: Exclude<DateRangePreset, 'custom'>) => {
    onChange(presetRange(preset, new Date()));
    setOpen(false);
  };

  // First click opens a new span, second closes it. Without the reset a third
  // click would extend a span the reader believes they restarted.
  const chooseDay = (day: Date) => {
    if (!start || end) {
      setStart(day);
      setEnd(null);
      return;
    }
    setEnd(day);
  };

  const apply = () => {
    if (!start) return;
    onChange(customRange(start, end ?? start));
    setOpen(false);
  };

  const canPageBack = addMonths(view, -1).getTime() >= addMonths(startOfDay(min), 0).getTime();
  const canPageForward = addMonths(view, MONTHS_SHOWN).getTime() <= startOfDay(ceiling).getTime();

  const shownMonths = Array.from({ length: MONTHS_SHOWN }, (_, offset) => dayKey(addMonths(view, offset)).slice(0, 7));

  const month = (offset: number): ReactNode => {
    const shown = addMonths(view, offset);
    return (
      <div className='bomdisoft-date-range-month' key={dayKey(shown)}>
        <p className='bomdisoft-date-range-month-name'>{months.format(shown)}</p>
        <div className='bomdisoft-date-range-weekdays'>
          {weekdayNames.map((name, index) => (
            <abbr key={`${name}-${index}`}>{name}</abbr>
          ))}
        </div>
        <div className='bomdisoft-date-range-grid'>
          {monthGrid(shown, weekStart).map(day => {
            const outside = day.getUTCMonth() !== shown.getUTCMonth();
            const selectable = isSelectable(day, min, ceiling);
            return (
              <button
                aria-label={cellFull.format(day)}
                aria-pressed={dayPosition(day, start, end) !== 'outside'}
                className='bomdisoft-date-range-day'
                data-day={dayKey(day)}
                data-outside={outside ? '' : undefined}
                data-position={dayPosition(day, start, end)}
                data-testid={outside && shownMonths.includes(dayKey(day).slice(0, 7)) ? undefined : `${testId ?? 'date-range'}-day-${dayKey(day)}`}
                data-today={isSameDay(day, ceiling) ? '' : undefined}
                disabled={!selectable}
                key={dayKey(day)}
                onClick={() => chooseDay(day)}
                type='button'
              >
                {cellDay.format(day)}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
      <button aria-controls={panelId} aria-expanded={open} aria-haspopup='dialog' className={triggerClassName ?? 'bomdisoft-date-range-trigger'} data-live={value.preset === 'live' ? '' : undefined} data-testid={testId} onClick={openPicker} ref={triggerRef} type='button'>
        <span aria-hidden='true' className='bomdisoft-date-range-trigger-icon'>
          {value.preset === 'live' ? <span className='bomdisoft-date-range-pulse' /> : <CalendarMonthRoundedIcon size={16} />}
        </span>
        <span className='bomdisoft-date-range-trigger-text'>{triggerText}</span>
        <span aria-hidden='true' className='bomdisoft-date-range-trigger-icon'>
          <KeyboardArrowDownRoundedIcon size={18} />
        </span>
      </button>

      <Menu anchorRef={triggerRef} className='bomdisoft-date-range-panel' id={panelId} label={label} onClose={() => setOpen(false)} open={open} role='dialog' testId={testId ? `${testId}-panel` : undefined}>
        <div className='bomdisoft-date-range-body'>
          {offered.length === 0 ? null : (
            <div className='bomdisoft-date-range-presets' data-testid={`${testId ?? 'date-range'}-presets`}>
              {offered.map(preset => (
                <button className='bomdisoft-date-range-preset' data-preset={preset} data-selected={value.preset === preset ? '' : undefined} data-testid={`${testId ?? 'date-range'}-preset-${preset}`} key={preset} onClick={() => choosePreset(preset)} type='button'>
                  {preset === 'live' ? <span aria-hidden='true' className='bomdisoft-date-range-pulse' /> : null}
                  {presetLabels[preset]}
                </button>
              ))}
            </div>
          )}

          <div className='bomdisoft-date-range-calendar'>
            <div className='bomdisoft-date-range-pager'>
              <Tooltip placement='top' title={previousMonthLabel}>
                <button aria-label={previousMonthLabel} className='bomdisoft-date-range-page' data-testid={testId ? `${testId}-previous` : undefined} disabled={!canPageBack} onClick={() => setView(addMonths(view, -1))} type='button'>
                  <ChevronLeftIcon size={18} />
                </button>
              </Tooltip>
              <span className='bomdisoft-date-range-spacer' />
              <Tooltip placement='top' title={nextMonthLabel}>
                <button aria-label={nextMonthLabel} className='bomdisoft-date-range-page' data-testid={testId ? `${testId}-next` : undefined} disabled={!canPageForward} onClick={() => setView(addMonths(view, 1))} type='button'>
                  <ChevronRightIcon size={18} />
                </button>
              </Tooltip>
            </div>
            <div className='bomdisoft-date-range-months'>{Array.from({ length: MONTHS_SHOWN }, (_, offset) => month(offset))}</div>
          </div>
        </div>

        <div className='bomdisoft-date-range-footer'>
          <p className='bomdisoft-date-range-summary' data-testid={testId ? `${testId}-summary` : undefined}>
            {start ? named(start, end ?? start) : ''}
          </p>
          <Button onClick={() => setOpen(false)} size='small' variant='text'>
            {cancelLabel}
          </Button>
          <Button data-testid={testId ? `${testId}-apply` : undefined} disabled={!start} onClick={apply} size='small' variant='contained'>
            {applyLabel}
          </Button>
        </div>
      </Menu>
    </>
  );
};
